import React, { useState } from 'react';
import { Save, Wrench, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { toast } from 'sonner';

export const MaintenanceTab = () => {
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceScheduled, setMaintenanceScheduled] = useState(false);
  const [maintenanceStartDate, setMaintenanceStartDate] = useState(null);
  const [maintenanceStartTime, setMaintenanceStartTime] = useState('00:00');
  const [maintenanceEndDate, setMaintenanceEndDate] = useState(null);
  const [maintenanceEndTime, setMaintenanceEndTime] = useState('23:59');
  const [maintenanceMessage, setMaintenanceMessage] = useState(
    'We are currently performing scheduled maintenance. Please check back soon!'
  );

  return (
    <Card className="rounded-xl border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Wrench className="h-5 w-5" />
          Maintenance Mode
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
          <div>
            <p className="font-medium text-slate-900">Enable Maintenance Mode</p>
            <p className="text-sm text-slate-600">Put the site into maintenance mode immediately</p>
          </div>
          <Switch
            checked={maintenanceEnabled}
            onCheckedChange={(checked) => {
              setMaintenanceEnabled(checked);
              toast.success(checked ? 'Maintenance mode enabled' : 'Maintenance mode disabled');
            }}
          />
        </div>

        {maintenanceEnabled && (
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-sm text-orange-800 font-medium">⚠️ Maintenance mode is currently active</p>
            <p className="text-xs text-orange-700 mt-1">Visitors will see the maintenance message instead of the site</p>
          </div>
        )}

        <div>
          <Label>Maintenance Message</Label>
          <Textarea
            value={maintenanceMessage}
            onChange={(e) => setMaintenanceMessage(e.target.value)}
            rows={4}
            className="mt-2"
            placeholder="Enter the message visitors will see during maintenance"
          />
          <p className="text-xs text-slate-500 mt-1">This message will be displayed to visitors during maintenance</p>
        </div>

        <div className="border-t pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-medium text-slate-900">Schedule Maintenance</p>
              <p className="text-sm text-slate-600">Set start and end times for automatic maintenance mode</p>
            </div>
            <Switch checked={maintenanceScheduled} onCheckedChange={setMaintenanceScheduled} />
          </div>

          {maintenanceScheduled && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date & Time</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal mt-2">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {maintenanceStartDate ? format(maintenanceStartDate, 'PPP') : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={maintenanceStartDate}
                        onSelect={setMaintenanceStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <Input
                    type="time"
                    value={maintenanceStartTime}
                    onChange={(e) => setMaintenanceStartTime(e.target.value)}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>End Date & Time (Auto-disable)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal mt-2">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {maintenanceEndDate ? format(maintenanceEndDate, 'PPP') : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={maintenanceEndDate}
                        onSelect={setMaintenanceEndDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <Input
                    type="time"
                    value={maintenanceEndTime}
                    onChange={(e) => setMaintenanceEndTime(e.target.value)}
                    className="mt-2"
                  />
                </div>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  <strong>Scheduled:</strong> Maintenance will automatically start on{' '}
                  {maintenanceStartDate ? format(maintenanceStartDate, 'PPP') : '(select date)'} at{' '}
                  {maintenanceStartTime} and end on {maintenanceEndDate ? format(maintenanceEndDate, 'PPP') : '(select date)'}{' '}
                  at {maintenanceEndTime}
                </p>
              </div>
            </div>
          )}
        </div>

        <Button onClick={() => toast.success('Maintenance settings saved')}>
          <Save className="h-4 w-4 mr-2" />
          Save Settings
        </Button>
      </CardContent>
    </Card>
  );
};